import PropTypes from 'prop-types';
import ReactApexChart from 'react-apexcharts';
// @mui
import { useTheme, styled } from '@mui/material/styles';
import { Card, CardHeader, Stack } from '@mui/material';
// utils
// import { fShortenNumber } from '../../../utils/formatNumber';
// components
import { useChart } from '../../../components/chart';
import { t } from 'i18next';
import { FormatNumber } from 'src/utils';
import EmtyData from 'src/components/emty-data';

// ----------------------------------------------------------------------

const CHART_HEIGHT = 372;
const LEGEND_HEIGHT = 100;

const StyledChartWrapper = styled('div')(({ theme }) => ({
    height: CHART_HEIGHT,
    marginTop: theme.spacing(1),
    '& .apexcharts-canvas svg': { height: CHART_HEIGHT },
    '& .apexcharts-canvas svg,.apexcharts-canvas foreignObject': {
        overflow: 'visible',
    },
    '& .apexcharts-legend': {
        height: LEGEND_HEIGHT,
        alignContent: 'center',
        overflow: 'hidden',
        position: 'relative !important',
        borderTop: `solid 1px ${theme.palette.divider}`,
        top: `calc(${CHART_HEIGHT - LEGEND_HEIGHT}px) !important`,
    },
}));

const StyledTitle = styled('div')(({ theme }) => ({
    fontSize: '30px', // Cỡ chữ mặc định
    [theme.breakpoints.down('md')]: {
        fontSize: '27px', // Cỡ chữ cho màn hình trung bình
    },
    [theme.breakpoints.down('sm')]: {
        fontSize: '18px', // Cỡ chữ cho màn hình nhỏ
    },
    [theme.breakpoints.down('xs')]: {
        fontSize: '16px', // Cỡ chữ cho màn hình rất nhỏ
    },
}));
const StyledSubheader = styled('div')(({ theme }) => ({
    fontSize: '23px', // Cỡ chữ mặc định
    whiteSpace: 'nowrap', // Giới hạn hiển thị chỉ một dòng
    overflow: 'hidden', // Ẩn phần văn bản vượt quá
    textOverflow: 'ellipsis', // Hiển thị dấu "..." nếu văn bản quá dài
    [theme.breakpoints.down('md')]: {
        fontSize: '25px', // Cỡ chữ cho màn hình trung bình
    },
    [theme.breakpoints.down('sm')]: {
        fontSize: '16px', // Cỡ chữ cho màn hình nhỏ
    },
    [theme.breakpoints.down('xs')]: {
        fontSize: '14px', // Cỡ chữ cho màn hình rất nhỏ
    },
}));

// ----------------------------------------------------------------------

AppCurrentVisits.propTypes = {
    title: PropTypes.string,
    subheader: PropTypes.string,
    chartColors: PropTypes.arrayOf(PropTypes.string),
    chartData: PropTypes.array,
};

export default function AppCurrentVisits({
    title,
    subheader,
    chartColors,
    totalLabel = t('total'),
    chartData,
    label = 'label',
    value = 'value',
    showLegend = true,
    ...other
}) {
    const theme = useTheme();

    console.log('chartData', chartData);
    const total = chartData.reduce(
        (accumulator, currentValue) => accumulator + (currentValue[value] ? currentValue[value] : 0),
        0
    );

    // Giới hạn hiển thị top 20 items, gộp còn lại vào "Khác" để tối ưu hiệu năng
    const MAX_ITEMS = 20;
    const sortedData = [...chartData].sort((a, b) => (b[value] || 0) - (a[value] || 0));
    const topItems = sortedData.slice(0, MAX_ITEMS);
    const otherItems = sortedData.slice(MAX_ITEMS);
    const otherValue = otherItems.reduce((sum, item) => sum + (item[value] || 0), 0);

    const displayData = otherValue > 0 ? [...topItems, { [label]: 'Khác', [value]: otherValue, symbol_currency: topItems[0]?.symbol_currency || '' }] : topItems;

    const chartLabels = displayData.map((i) => {
        const amount = i[value] ?? 0;
        const formattedNumber = FormatNumber(amount);
        const currency = i?.symbol_currency ?? '';

        const suffix = value.toLowerCase().includes('price') ? ` ${currency}` : '';

        return `${i[label]} - ${formattedNumber}${suffix}`;
    });

    const chartSeries = displayData.map((i) => (i[value] ? i[value] : 0));

    const chartOptions = useChart({
        colors: chartColors,
        labels: chartLabels,
        stroke: { colors: [theme.palette.background.paper] },
        legend: {
            floating: true,
            position: 'bottom', // Chuyển legend xuống dưới
            horizontalAlign: 'center', // Căn giữa
            height: LEGEND_HEIGHT + 80, // Tăng chiều cao legend để tránh chồng
            width: undefined, // Đảm bảo không giới hạn kích thước
            fontSize: '12px', // Giảm kích thước font
            itemMargin: {
                horizontal: 8, // Khoảng cách ngang giữa các mục
                vertical: 4, // Khoảng cách dọc giữa các mục
            },
            onItemClick: {
                toggleDataSeries: true, // Giữ tính năng bật/tắt series khi click vào legend
            },
            onItemHover: {
                highlightDataSeries: true, // Highlight series khi hover vào legend
            },
        },

        dataLabels: {
            enabled: true,
            dropShadow: { enabled: true },
            style: {
                fontSize: '14px',
            },
        },
        tooltip: {
            fillSeriesColor: true,
            y: {
                formatter: (seriesName) => ``,
                title: {
                    formatter: (seriesName) => `${seriesName}`,
                },
            },
            onDatasetHover: {
                highLightDataSeries: true,
            },
        },

        plotOptions: {
            pie: {
                donut: {
                    labels: {
                        show: true,
                        name: {
                            formatter: function (val) {
                                return val.split(' (')[0];
                            },
                        },
                        total: {
                            label: totalLabel,
                            formatter: function (w) {
                                // return w.globals.seriesTotals.reduce((a, b) => {
                                //     return fShortenNumber(a + b);
                                // }, 0);
                                return FormatNumber(total);
                            },
                        },
                        value: {
                            formatter: function (val) {
                                return FormatNumber(val);
                            },
                            // offsetY: 8, // Điều chỉnh vị trí theo chiều dọc
                        },
                    },
                    size: '60%',
                },
            },
        },
    });

    return (
        <Card {...other}>
            <CardHeader
                title={<StyledTitle>{title}</StyledTitle>}
                subheader={<StyledSubheader>{subheader}</StyledSubheader>}
            />
            {displayData?.length > 0 && chartSeries?.length > 0 ? (
                <StyledChartWrapper dir="ltr">
                    <ReactApexChart type="donut" series={chartSeries} options={chartOptions} height={280} />
                </StyledChartWrapper>
            ) : (
                <Stack padding={5}>
                    <EmtyData />
                </Stack>
            )}
            {/* <Grid container spacing={0.5} padding={1}>
                <Grid item xl={12} md={12} sm={12} style={{ width: '100%' }}>
                    <StyledChartWrapper dir="ltr">
                        <ReactApexChart type="donut" series={chartSeries} options={chartOptions} height={300} />
                    </StyledChartWrapper>
                </Grid>
                <Grid item xl={6} md={6} sm={12} style={{ width: '100%', maxHeight: '50vh', overflowY: 'scroll' }}>
                    <TableComponent
                        isFooter={true}
                        total={table.data.length}
                        cols={table.cols}
                        dataTable={table.data}
                    />
                </Grid>
            </Grid> */}
        </Card>
    );
}
